// CreateMemberEventAttract.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberEventAttract.module.scss";
import { EVENT_ATTRACT } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q15) */
import mrImg from "@/assets/UI/memberCreate/q15Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q15Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q15MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q15MrsM.webp";

/* ---- Framer animations (same feel as SexualEnvironment) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

function toggleChoice(current, value) {
  if (current.includes(value)) return current.filter((v) => v !== value);
  return [...current, value];
}

/* Emoji meta for event-attract options */
const EVENT_META = {
  meeting_open_minded: "💬",
  flirty_conversations: "🍸",
  dancing_social_spark: "💃",
  exploring_connections: "🔥",
  watching_energy: "🕶️",
  mystery_freedom: "🌌",
  all_of_above: "💫",
};

/* Fallback English labels */
const EVENT_LABELS = {
  meeting_open_minded: "Meeting new, open-minded people",
  flirty_conversations: "Flirty conversations & subtle glances",
  dancing_social_spark: "Dancing and enjoying the social spark",
  exploring_connections: "Exploring sensual connections with chemistry",
  watching_energy: "Watching the energy and soaking in the atmosphere",
  mystery_freedom: "The mystery, freedom & no-judgment vibe",
  all_of_above: "All of the above — I love the whole experience",
};

/* Closing text key helper – personalized by member’s gender */
function getClosingKey(rawGender, isCouple) {
  if (isCouple) return "onboarding.event_attract.closing.couple";
  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.event_attract.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.event_attract.closing.woman";
  }
  return "onboarding.event_attract.closing.other";
}

export default function CreateMemberEventAttract() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoices, setMemberChoices] = useState([]); // string[]
  const [partnerChoices, setPartnerChoices] = useState([]); // string[]

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.event_attract.title", {
    defaultValue: "What pulls you in at events",
  });

  const intro1 = t("onboarding.event_attract.intro1", {
    defaultValue:
      "Different people get turned on by different parts of the vibe.",
  });
  const intro2 = t("onboarding.event_attract.intro2", {
    defaultValue:
      "Pick what makes you smile first. You can choose more than one.",
  });
  const intro3 = t("onboarding.event_attract.intro3", {
    defaultValue: "This helps us match your suggestions to your real energy.",
  });

  const singleQuestion = t("onboarding.event_attract.single.question", {
    defaultValue: "What do you enjoy most during lifestyle events",
  });

  const coupleLead = t("onboarding.event_attract.couple.lead", {
    defaultValue:
      "Pick your favourites… then let your partner pick theirs too.",
  });
  const coupleDetail = t("onboarding.event_attract.couple.detail", {
    defaultValue: "Two people can love two different parts of the same night.",
  });

  const partnerLabel = t("onboarding.event_attract.partner.label", {
    defaultValue: "Now pick what your partner enjoys most",
  });

  const guideTitle = t("onboarding.event_attract.guide.title", {
    defaultValue: "Quick note",
  });
  const guideBody = t("onboarding.event_attract.guide.body", {
    defaultValue:
      "This doesn’t lock you in. It simply shapes your matches and suggestions to feel more like your kind of night.",
  });

  const baseOptions =
    Array.isArray(EVENT_ATTRACT) && EVENT_ATTRACT.length
      ? EVENT_ATTRACT
      : Object.keys(EVENT_LABELS);

  const options = baseOptions.map((value) => ({
    value,
    emoji: EVENT_META[value] || "✨",
    title: t(`onboarding.event_attract.options.${value}.label`, {
      defaultValue: EVENT_LABELS[value] || value,
    }),
    hint: t(`onboarding.event_attract.tapToSelect.${value}`, {
      defaultValue: "Tap to add this to your event vibe",
    }),
  }));

  const hasMember = memberChoices.length > 0;
  const hasPartner = partnerChoices.length > 0;

  const closingKey =
    hasMember && (!isCouple || hasPartner)
      ? getClosingKey(rawGender, isCouple)
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  const submitDisabled =
    submitting || !memberChoices.length || (isCouple && !partnerChoices.length);

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info(
        t("onboarding.event_attract.toast.member_required", {
          defaultValue: "Pick at least one thing you enjoy.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoices.length) {
      toast.info(
        t("onboarding.event_attract.toast.partner_required", {
          defaultValue: "Now let your partner pick at least one too.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberEventAttract: memberChoices,
          partnerEventAttract: partnerChoices,
        }
      : {
          memberEventAttract: memberChoices,
        };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/event_attract",
        payload,
        { skipToast: true },
      );

      await refreshMe();
      navigate(data?.next || "/member/alcohol_cannabis", { replace: true });
    } catch (err) {
      console.error("update event attract failed:", err);
      toast.error(
        t("onboarding.event_attract.toast.save_error", {
          defaultValue: "We couldn’t save this. Please try again.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderOptionsBlock({ name, subtitleText, choices, setChoices }) {
    return (
      <div className={cls.block}>
        {subtitleText ? (
          <div className={cls.blockTitle}>{subtitleText}</div>
        ) : null}

        <fieldset className={cls.options}>
          <legend className="sr-only">{subtitleText || title}</legend>

          {options.map((opt) => {
            const checked = choices.includes(opt.value);

            return (
              <label
                key={`${name}-${opt.value}`}
                className={`${cls.option} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="checkbox"
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={() =>
                    setChoices((prev) => toggleChoice(prev, opt.value))
                  }
                />

                <span className={cls.optionBody}>
                  <span className={cls.optionEmoji} aria-hidden="true">
                    {opt.emoji}
                  </span>

                  <span className={cls.optionText}>
                    <strong>{opt.title}</strong>
                    <small>{opt.hint}</small>
                  </span>

                  <span className={cls.optionDot} aria-hidden="true" />
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>

              <div className={cls.formSub}>{intro1}</div>
              <div className={cls.formSub}>{intro2}</div>
              <div className={cls.formSub}>{intro3}</div>

              {!isCouple ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {singleQuestion}
                </div>
              ) : (
                <>
                  <div className={cls.formSub} style={{ marginTop: 8 }}>
                    {coupleLead}
                  </div>
                  <div className={cls.formSub}>{coupleDetail}</div>
                </>
              )}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {renderOptionsBlock({
              name: "memberEventAttract",
              subtitleText: singleQuestion,
              choices: memberChoices,
              setChoices: setMemberChoices,
            })}

            {isCouple &&
              renderOptionsBlock({
                name: "partnerEventAttract",
                subtitleText: partnerLabel,
                choices: partnerChoices,
                setChoices: setPartnerChoices,
              })}

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
